import {useRef, useState} from 'react';
import {Box, IconButton} from '@mui/material';
import {Worker, Viewer} from '@react-pdf-viewer/core';
import {zoomPlugin} from '@react-pdf-viewer/zoom';
import {printPlugin} from '@react-pdf-viewer/print';
import {pageNavigationPlugin} from '@react-pdf-viewer/page-navigation';
import {ReactComponent as FullscreenIcon} from '@icons/full-screen.svg';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';

import {PDFStyle} from './PDF.style';

interface PdfViewerProps {
	fileUrl: string;
	isQR?: boolean;
}

const PdfViewerInner = ({fileUrl, isQR}: PdfViewerProps) => {
	const zoomPluginInstance = useRef(zoomPlugin()).current;
	const printPluginInstance = useRef(printPlugin()).current;
	const pageNavigationPluginInstance = useRef(pageNavigationPlugin()).current;

	const plugins = useRef([zoomPluginInstance, printPluginInstance, pageNavigationPluginInstance]).current;

	const {ZoomIn, ZoomOut} = zoomPluginInstance;
	const {Print} = printPluginInstance;
	const {GoToNextPage, GoToPreviousPage, CurrentPageInput, NumberOfPages} = pageNavigationPluginInstance;

	const containerRef = useRef<HTMLDivElement>(null);

	const [isFullscreen, setIsFullscreen] = useState(false);

	const handleFullScreen = () => {
		if (document.fullscreenElement) {
			document.exitFullscreen();
			setIsFullscreen(false);
		} else {
			containerRef.current?.requestFullscreen();
			setIsFullscreen(true);
		}
	};

	return (
		<Box
			ref={containerRef}
			sx={{
				...PDFStyle.wrapper,
				backgroundColor: '#fff',
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				width: '100%',
			}}
		>
			<Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
				<Box sx={{...PDFStyle.viewerBox, flexGrow: 1, height: '100%', minHeight: '300px'}}>
					<Viewer fileUrl={fileUrl} plugins={plugins} renderLoader={() => <Box />} />
				</Box>

				<Box sx={PDFStyle.toolbarWrapper}>
					<Box sx={PDFStyle.toolBarBox}>
						<Box sx={{display: 'flex'}}>
							<ZoomOut />
							<ZoomIn />
						</Box>

						{!isQR && (
							<Box sx={PDFStyle.toolBarMiddleBlock}>
								<GoToPreviousPage />
								<Box sx={{display: 'flex', alignItems: 'center', mx: 1}}>
									<CurrentPageInput /> / <NumberOfPages />
								</Box>
								<GoToNextPage />
							</Box>
						)}

						<Box sx={{display: 'flex', alignItems: 'center'}}>
							<Print />
							<IconButton onClick={handleFullScreen} size="small">
								<FullscreenIcon style={{width: 16, height: 16}} />
							</IconButton>
						</Box>
					</Box>
				</Box>
			</Worker>
		</Box>
	);
};

const PdfViewer = (props: PdfViewerProps) => <PdfViewerInner key={props.fileUrl} {...props} />;

export default PdfViewer;
